/**
 * Address Page - Router
 * 
 * Routes between legacy and V2 layouts based on experiment availability.
 * 
 * **Routing Logic:**
 * - If ANY address page experiment is enabled for tenant → Render V2
 * - If NO address page experiments are enabled → Render legacy (safety fallback)
 * 
 * This provides a catch condition: if all experiments are disabled/fail,
 * users see the stable legacy page instead of experiment-less V2.
 * 
 * Each section handles its own skeleton during hydration to prevent FOUC.
 * This is the entry point for the address page.
 */

import { useState, useMemo } from 'react';
import { ComplianceRequirement } from 'types/compliance';
import Listing from 'data/listing';
import { AddressPageProps } from './address-page.types';
import type { AddressPageCommonData, AddressPageEventHandlers } from './shared';
import { DEFAULT_FEATURE_FLAGS } from './types';
import { AddressPageV2 } from './v2';
import ListingSourceBanner from 'components/dev/listing-source-banner';
import type { PriceHistory } from './components/price-history';

/** Re-export TAB_IDS for backwards compatibility */
export { TAB_IDS } from './constants';

type ListingSourceType = 'go-search' | 'client-app-fallback';

export default function AddressPage(props: AddressPageProps) {
  const {
    tenantId,
    address,
    activeListing: { listing: defaultListing, isSaved = false },
    history: defaultHistory,
    breadcrumbs,
    secondaryInfo: {
      schools,
      neighbourhoodDemographics,
    },
    appraisal,
    stats: {
      municipality,
      neighbourhood,
    },
    featureFlags: {
      flags = DEFAULT_FEATURE_FLAGS,
      internalLinks = [],
    },
    compliance: {
      showTermsOfUseBanner = false,
      shouldScramble = false,
      onTermsOfUseBannerDismiss,
      complianceRequirement = ComplianceRequirement.NONE,
      onComplianceRequirementClick,
      hideSquareFootage,
    },
    experiments: {
      soldListingExperimentId = undefined,
    } = {},
    onSeeAllSavedListingsClick,
    onSaveListingClick,
    onContactForPriceClick,
    onViewListingClick,
    onSwiperCarouselImageClick,
    onRoomsShowDetailsClick,
    onSimilarListingsCardClick,
    qaComparison,
  } = props;

  const [qaActiveSource, setQaActiveSource] = useState<ListingSourceType>(
    (props.listingSource as ListingSourceType) || 'go-search'
  );

  const qaData = useMemo(() => {
    if (!qaComparison) return null;
    const dataset = qaActiveSource === 'go-search' ? qaComparison.goSearch : qaComparison.clientApp;
    if (!dataset.activeListing) return null;
    return {
      listing: Listing.fromJSON(dataset.activeListing.listing),
      history: dataset.history.map(h => ({
        ...h,
        addedAt: h.addedAt != null ? new Date(h.addedAt as unknown as string) : null,
        expiredAt: h.expiredAt != null ? new Date(h.expiredAt as unknown as string) : null,
        soldAt: h.soldAt != null ? new Date(h.soldAt as unknown as string) : null,
      })) as unknown as readonly PriceHistory<Listing>[],
    };
  }, [qaComparison, qaActiveSource]);

  const listing = qaData?.listing ?? defaultListing;
  const history = qaData?.history ?? defaultHistory;

  const commonData: AddressPageCommonData = {
    tenantId,
    address,
    listing,
    isSaved,
    history,
    breadcrumbs,
    schools,
    neighbourhoodDemographics,
    municipality,
    neighbourhood,
    appraisal,
    flags,
    internalLinks,
    showTermsOfUseBanner,
    shouldScramble,
    complianceRequirement,
    soldListingExperimentId,
    hideSquareFootage,
    listingSource: props.listingSource,
  };

  const handlers: AddressPageEventHandlers = {
    onSeeAllSavedListingsClick,
    onSaveListingClick,
    onContactForPriceClick,
    onViewListingClick,
    onSwiperCarouselImageClick,
    onRoomsShowDetailsClick,
    onSimilarListingsCardClick,
    onTermsOfUseBannerDismiss,
    onComplianceRequirementClick,
  };

  return <>
    <ListingSourceBanner
      source={props.listingSource}
      qaComparison={qaComparison}
      activeSource={qaActiveSource}
      onTabChange={setQaActiveSource}
    />
    <AddressPageV2 data={commonData} handlers={handlers} />
  </>;
}
